import * as Yup from 'yup'
import React, { useState, useEffect, useRef } from 'react'
import { useForm, UseFormTrigger } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  InputAdornment,
  Stack,
  IconButton,
  Alert,
  Button,
  Link as MuiLink,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { AccountInfo } from '@src/types/account'
import { getReferralCode, setReferralCode } from '@src/utils/jwt'
import { useBoolean } from '@src/hooks/common/useBoolean'
import RHFTextField from '@src/components/hook-form/rhf-text-field'
import Iconify from '@src/components/iconify'
import FormProvider from '@src/components/hook-form/form-provider'
import { registerCode, register, emailLogin, forgotPassword } from '@src/service/auth'

import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

const SendCodeButton = ({
  username,
  triggerUsername,
  onError,
}: {
  username: string | undefined
  triggerUsername: () => Promise<boolean>
  onError: (message: string) => void
}) => {
  const { t } = useTranslation()
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const startCountdown = () => {
    setCountdown(60)
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleClick = async () => {
    if (countdown > 0 || isSendingCode) return

    onError('')
    const emailValid = await triggerUsername()
    if (!emailValid) return

    if (!username) {
      onError(t('login.email_login.validation.email_required'))
      return
    }

    setIsSendingCode(true)
    try {
      const res = await registerCode(username)
      if (res.code === 0) {
        toast.success(t('login.email_login.success.code_sent'))
        startCountdown()
      } else {
        onError(res.msg || t('login.email_login.error.send_code_failed'))
      }
    } catch (error) {
      onError(typeof error === 'string' ? error : (error as Error).message)
    } finally {
      setIsSendingCode(false)
    }
  }

  const buttonText = () => {
    if (isSendingCode) return t('login.email_login.button.sending')
    if (countdown > 0) return `${countdown}s`
    return t('login.email_login.button.send_code')
  }

  return (
    <Button
      onClick={handleClick}
      disabled={isSendingCode || countdown > 0}
      sx={{
        minWidth: '70px',
      }}
    >
      {buttonText()}
    </Button>
  )
}

type LoginInProps = {
  password: ReturnType<typeof useBoolean>
  currentUsername: string | undefined
  trigger: UseFormTrigger<any>
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>
  resetPasswordSucc: (email: string) => void
}

const LoginIn = ({
  password,
  currentUsername,
  trigger,
  setErrorMsg,
  resetPasswordSucc,
}: LoginInProps) => {
  const { t } = useTranslation()
  const [isSubmittingForgot, setIsSubmittingForgot] = useState(false)

  const handleForgotPasswordClick = async () => {
    setErrorMsg('')
    setIsSubmittingForgot(true)
    try {
      const emailValid = await trigger('username')
      if (!emailValid || !currentUsername) {
        setErrorMsg(t('login.email_login.error.enter_valid_email'))
        setIsSubmittingForgot(false)
        return
      }

      const res = await forgotPassword({ username: currentUsername })
      if (res.code === 0) {
        toast.success(t('login.email_login.success.reset_instructions_sent'))
        resetPasswordSucc(currentUsername)
      } else {
        setErrorMsg(res.msg || t('login.email_login.error.send_reset_failed'))
      }
    } catch (error) {
      setErrorMsg(typeof error === 'string' ? error : (error as Error).message)
    } finally {
      setIsSubmittingForgot(false)
    }
  }

  return (
    <>
      <Stack spacing={2.5} mt={4}>
        <RHFTextField name='username' label={t('login.email_login.label.email')} />
        <RHFTextField
          name='password'
          label={t('login.email_login.label.password')}
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={password.onToggle} edge='end'>
                  <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      {/* Forgot Password Link */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <MuiLink
          onClick={handleForgotPasswordClick}
          variant='body2'
          color='text.secondary'
          underline='hover'
          sx={{ cursor: 'pointer' }}
          component='button'
          disabled={isSubmittingForgot}
        >
          {isSubmittingForgot
            ? t('login.email_login.button.sending')
            : t('login.email_login.button.forgot_password')}
        </MuiLink>
      </Box>
    </>
  )
}

type CreateAccountProps = {
  password: ReturnType<typeof useBoolean>
  currentUsername: string | undefined
  trigger: UseFormTrigger<any>
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>
}

const CreateAccount = ({ password, currentUsername, trigger, setErrorMsg }: CreateAccountProps) => {
  const { t } = useTranslation()
  return (
    <>
      <Stack spacing={2.5} mt={4}>
        <RHFTextField name='username' label={t('login.email_login.label.email')} />
        <RHFTextField
          name='password'
          label={t('login.email_login.label.password')}
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={password.onToggle} edge='end'>
                  <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <RHFTextField
          name='code'
          label={t('login.email_login.label.verification_code')}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <SendCodeButton
                  username={currentUsername}
                  triggerUsername={() => trigger('username')}
                  onError={setErrorMsg}
                />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </>
  )
}

export default function EmailLogin({
  loginSuccess,
  resetPasswordSucc,
}: {
  loginSuccess: (data: AccountInfo) => void
  resetPasswordSucc: (email: string) => void
}) {
  const { t } = useTranslation()
  const [errorMsg, setErrorMsg] = useState('')
  const password = useBoolean()
  const referralCode = getReferralCode()
  const [showCreateAccount, setShowCreateAccount] = useState(false)

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .required(t('login.email_login.validation.email_required'))
      .email(t('login.email_login.validation.email_invalid')),
    password: Yup.string()
      .required(t('login.email_login.validation.password_required'))
      .min(8, t('login.email_login.validation.password_min_length')),
    code: Yup.string().when('$showCreateAccount', {
      is: true,
      then: (schema) => schema.required(t('login.email_login.validation.code_required')),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  const defaultValues = {
    username: '',
    password: '',
    code: '',
  }

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
    context: { showCreateAccount },
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    reset,
    watch,
  } = methods

  const currentUsername = watch('username')

  const onSubmit = handleSubmit(async (data) => {
    setErrorMsg('')
    try {
      if (showCreateAccount && data.code) {
        const params = { username: data.username, password: data.password, code: data.code }
        const res = await register(
          referralCode
            ? {
                ...params,
                referralCode,
              }
            : params,
        )
        if (res.code === 0) {
          toast.success(t('login.email_login.success.account_created'))
          setShowCreateAccount(false)
          reset({ username: data.username, password: '', code: '' })
          setReferralCode(null)
        } else {
          setErrorMsg(res.msg || t('login.email_login.error.registration_failed'))
        }
      } else {
        const res = await emailLogin({ username: data.username, password: data.password })
        if (res.code === 0 && res.data) {
          loginSuccess(res.data)
        } else {
          setErrorMsg(res.msg || t('login.email_login.error.login_failed'))
        }
      }
    } catch (error) {
      setErrorMsg(typeof error === 'string' ? error : (error as Error).message)
    }
  })

  const handleToggleView = () => {
    setErrorMsg('')
    reset(defaultValues)
    setShowCreateAccount(!showCreateAccount)
  }

  return (
    <>
      {!!errorMsg && (
        <Alert severity='error' sx={{ mb: 3, mt: 2 }}>
          {errorMsg}
        </Alert>
      )}

      <FormProvider methods={methods} onSubmit={onSubmit}>
        {showCreateAccount ? (
          <CreateAccount
            password={password}
            currentUsername={currentUsername}
            trigger={trigger}
            setErrorMsg={setErrorMsg}
          />
        ) : (
          <LoginIn
            password={password}
            currentUsername={currentUsername}
            trigger={trigger}
            setErrorMsg={setErrorMsg}
            resetPasswordSucc={resetPasswordSucc}
          />
        )}
        <Box textAlign='center' mt={2}>
          <LoadingButton
            sx={{ height: 40 }}
            fullWidth
            color='inherit'
            size='small'
            type='submit'
            variant='contained'
            loading={isSubmitting}
          >
            {showCreateAccount
              ? t('login.email_login.button.create_account')
              : t('login.email_login.button.sign_in')}
          </LoadingButton>
        </Box>

        <Box textAlign='center'>
          <Box
            component='a'
            onClick={handleToggleView}
            sx={{
              mt: 2,
              fontSize: 14,
              color: '#6c7cff',
              textDecoration: 'none',
              cursor: 'pointer',

              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {showCreateAccount
              ? t('login.email_login.button.back_to_sign_in')
              : t('login.email_login.button.create_account_link')}
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}
